import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { bmOfAccount } from "@/data/businessManagers";
import { getCampaignClients, subscribeCampaigns } from "@/lib/campaignData";

// Controlla SOLO la visibilità nella UI (la sincronizzazione importa sempre tutto).
const VisibilityContext = createContext(null);
const KEY = "aesthetic-os-visibility";

const load = () => {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) || "{}");
    return { bms: new Set(p.bms || []), accounts: new Set(p.accounts || []) };
  } catch {
    return { bms: new Set(), accounts: new Set() };
  }
};

export function VisibilityProvider({ children }) {
  const [hidden, setHidden] = useState(load);
  const [tick, force] = useState(0);
  useEffect(() => subscribeCampaigns(() => force((x) => x + 1)), []);

  const persist = (next) => {
    localStorage.setItem(KEY, JSON.stringify({ bms: [...next.bms], accounts: [...next.accounts] }));
    setHidden(next);
  };

  const toggleBM = (bmId) => {
    const bms = new Set(hidden.bms);
    bms.has(bmId) ? bms.delete(bmId) : bms.add(bmId);
    persist({ bms, accounts: hidden.accounts });
  };
  const toggleAccount = (actId) => {
    const accounts = new Set(hidden.accounts);
    accounts.has(actId) ? accounts.delete(actId) : accounts.add(actId);
    persist({ bms: hidden.bms, accounts });
  };

  // Imposta esplicitamente la visibilità di un account (usato da Account Routing).
  const setAccountVisible = (actId, visible) => {
    const accounts = new Set(hidden.accounts);
    visible ? accounts.delete(actId) : accounts.add(actId);
    persist({ bms: hidden.bms, accounts });
  };

  const isBMVisible = (bmId) => !hidden.bms.has(bmId);
  const isAccountVisible = (actId) => !hidden.accounts.has(actId) && !hidden.bms.has(bmOfAccount[actId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleClients = useMemo(
    () => getCampaignClients().filter((c) => !hidden.accounts.has(c.adAccountId) && !hidden.bms.has(bmOfAccount[c.adAccountId])),
    [hidden, tick]
  );

  return (
    <VisibilityContext.Provider
      value={{ hidden, toggleBM, toggleAccount, setAccountVisible, isBMVisible, isAccountVisible, visibleClients }}
    >
      {children}
    </VisibilityContext.Provider>
  );
}

export const useVisibility = () => useContext(VisibilityContext);
